// @flow
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withRouter } from 'react-router-dom';

import { Table } from 'moods-react/Table';
import { Button, buttonTypes, buttonSizes } from 'moods-react/Button';

import orderMessages from '../../data/orderhistory';
import orderStatus from '../data/OrderHistoryStatus';
import { envUrl } from '../services/environment/EnvironmentService';
import DashboardLoadingPlaceholder from './DashboardLoadingPlaceholderComponent';

import './OrderHistoryComponent.scss';

type OrderItem = {
  orderReference: string,
  status: string,
  name: string,
  transactionAmount: string,
};

type Props = {
  isLoading: boolean,
  title: string,
  items: Array<OrderItem>,
  history: Object,
};

type Context = {
  intl: Object,
};

const getOrderStatusText = (status: string) => {
  if (Object.keys(orderStatus).includes(status)) {
    return orderMessages[orderStatus[status].text];
  }
  return orderMessages[orderStatus.unknown.text];
};

const getTableRowPlaceholder = () => (
  <Table.Row className="order-history__row">
    <Table.Cell className="order-history__cell">
      <DashboardLoadingPlaceholder className="u-marginTop-xs" type="single" />
    </Table.Cell>
    <Table.Cell className="order-history__cell">
      <DashboardLoadingPlaceholder className="u-marginTop-xs" type="single" />
    </Table.Cell>
    <Table.Cell className="order-history__cell">
      <DashboardLoadingPlaceholder className="u-marginTop-xs" type="single" />
    </Table.Cell>
    <Table.Cell className="order-history__cell">
      <DashboardLoadingPlaceholder className="u-marginTop-xs" type="dot-dash" />
    </Table.Cell>
  </Table.Row>
);

const getTableRows = (items: Array<OrderItem>, formatMessage: Function) =>
  items.map((item) => {
    const { orderReference, status, name, transactionAmount } = item;
    const statusText = getOrderStatusText(status);
    return (
      <Table.Row className="order-history__row" key={orderReference}>
        <Table.Cell className="order-history__cell">
          <a href={envUrl(`/orders/details/${orderReference}`, '/m4b')}>
            <span className="order-history__reference-link">#{orderReference}</span>
          </a>
        </Table.Cell>
        <Table.Cell className="order-history__cell u-fontFamily-secondaryMedium">{name}</Table.Cell>
        <Table.Cell className="order-history__cell order-history__price u-fontFamily-secondary">
          {transactionAmount}
        </Table.Cell>
        <Table.Cell className="order-history__cell">
          <div className="order-history__order-status">
            <span className={`order-history__circle order-history__circle--${orderStatus[status].color || 'red'}`} />
            <span className="u-fontFamily-secondary u-marginLeft-xxxs">{formatMessage(statusText)}</span>
          </div>
        </Table.Cell>
      </Table.Row>
    );
  });

const getEmptyTable = (formatMessage: Function, history: Object) => (
  <div className="order-history__empty-table u-paddingBottom-m">
    <span className="u-marginBottom-m">{formatMessage(orderMessages.noOrders)}</span>
    <Button
      className="u-width-auto@medium"
      type={buttonTypes.PRIMARY}
      size={buttonSizes.MEDIUM}
      isFullWidth
      onClick={() => history.push(envUrl('/products'))}
    >
      {formatMessage(orderMessages.startOrdering)}
    </Button>
  </div>
);

const getOrdersTable = (hasOrders: boolean, items: Array<OrderItem>, formatMessage: Function, history: Object) => {
  if (!hasOrders) {
    return getEmptyTable(formatMessage, history);
  }

  return (
    <div className="order-history__table-overflow">
      <Table horizontalBorders largePadding className="order-history__table">
        <Table.Body>{getTableRows(items, formatMessage)}</Table.Body>
      </Table>
    </div>
  );
};

const getTablePlaceholder = () => (
  <div className="order-history__table-overflow">
    <Table horizontalBorders largePadding className="order-history__table">
      <Table.Body>
        {getTableRowPlaceholder()}
        {getTableRowPlaceholder()}
        {getTableRowPlaceholder()}
      </Table.Body>
    </Table>
  </div>
);

const OrderHistoryComponent = ({ isLoading, title, items, history }: Props, context: Context) => {
  const { formatMessage } = context.intl;
  const hasOrders = items.length > 0;
  const classNames = classnames('order-history', 'u-marginTop-s', 'u-marginTop-m@large', {
    'order-history--has-orders': hasOrders,
  });

  return (
    <div className={classNames}>
      <div className="order-history__title-container u-marginBottom-s u-flexDirection-row@medium">
        <span className="u-fontFamily-primaryMedium u-fontSize-xl u-paddingBottom-s">{title}</span>
        {!isLoading && hasOrders && (
          <Button
            type={buttonTypes.STANDALONE_WHITE}
            size={buttonSizes.SMALL}
            href={envUrl('/orders', '/m4b')}
            className="u-marginBottom-s"
          >
            {formatMessage(orderMessages.viewOrders)}
          </Button>
        )}
      </div>
      {isLoading && getTablePlaceholder()}
      {!isLoading && getOrdersTable(hasOrders, items, formatMessage, history)}
    </div>
  );
};

OrderHistoryComponent.displayName = 'OrderHistory';

OrderHistoryComponent.contextTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }),
};

export default withRouter(OrderHistoryComponent);
